import type {BadgeProps} from "@/components/ui/badge";
import {
  resolveRowAmenityAccessRestricted,
  resolveRowAmenityAccessRestrictedAt,
  type AmenityRestrictionRowSource,
} from "@/src/modules/households/lib/household-amenity-restriction";
import {
  getPermanentPermitCoverageStatus,
  getPermanentPermitStartHumanLabel,
} from "./compute-permanent-permit-coverage";
import type {PermanentParkingPermit, PermanentParkingPermitStatus} from "../types";

/** Staff copy when an active permit shows Suspended due to household amenity restriction. */
export const PERMIT_AMENITY_DISPLAY_SUSPEND_REASON =
  "Suspended due to household amenity access restriction.";

export function isPermitAmenityDisplaySuspended(
  status: PermanentParkingPermitStatus,
  source: AmenityRestrictionRowSource | null | undefined,
): boolean {
  if (status === "suspended") return false;
  return resolveRowAmenityAccessRestricted(source) && status === "active";
}

export function isPermitSystemSuspended(status: PermanentParkingPermitStatus): boolean {
  return status === "suspended";
}

export type PermitSuspendDisplaySource = AmenityRestrictionRowSource & {
  status: PermanentParkingPermitStatus;
  suspend_reason?: string | null;
  suspended_at?: string | null;
  suspended_by?: {id?: number; name?: string; user_name?: string} | null;
};

export function getPermitSuspendReasonForDisplay(
  source: PermitSuspendDisplaySource | null | undefined,
): string | null | undefined {
  if (!source) return undefined;
  if (isPermitAmenityDisplaySuspended(source.status, source)) {
    return PERMIT_AMENITY_DISPLAY_SUSPEND_REASON;
  }
  return source.suspend_reason;
}

export function getPermitSuspendedAtForDisplay(
  source: PermitSuspendDisplaySource | null | undefined,
): string | null | undefined {
  if (!source) return undefined;
  if (isPermitAmenityDisplaySuspended(source.status, source)) {
    return resolveRowAmenityAccessRestrictedAt(source);
  }
  return source.suspended_at;
}

export function getPermitSuspendedByForDisplay(
  source: PermitSuspendDisplaySource | null | undefined,
): PermitSuspendDisplaySource["suspended_by"] {
  if (!source) return undefined;
  if (isPermitAmenityDisplaySuspended(source.status, source)) {
    return null;
  }
  return source.suspended_by;
}

export function getPermitStatusActionFlags(status: PermanentParkingPermitStatus) {
  return {
    canReactivate: status === "suspended",
    canSuspend: status === "active" || status === "scheduled",
    canRevoke: ["active", "suspended", "scheduled", "expired"].includes(status),
  };
}

/** Action flags — suspend disabled on amenity overlay; reactivate blocked while household still restricted. */
export function getPermitStatusActionFlagsForDisplay(
  status: PermanentParkingPermitStatus,
  source: AmenityRestrictionRowSource | null | undefined,
) {
  const flags = getPermitStatusActionFlags(status);
  if (isPermitAmenityDisplaySuspended(status, source)) {
    return {...flags, canSuspend: false};
  }
  if (status === "suspended" && resolveRowAmenityAccessRestricted(source)) {
    return {...flags, canReactivate: false};
  }
  return flags;
}

export function getPermitStatusBadgeProps(
  status: PermanentParkingPermitStatus,
): {label: string; variant: BadgeProps["variant"]} {
  switch (status) {
    case "active":
      return {label: "Active", variant: "success"};
    case "suspended":
      return {label: "Suspended", variant: "warning"};
    case "revoked":
      return {label: "Revoked", variant: "error"};
    case "scheduled":
      return {label: "Scheduled", variant: "info"};
    case "expired":
      return {label: "Expired", variant: "secondary"};
    default:
      return {label: "Unknown", variant: "secondary"};
  }
}

/** Display badge — API `suspended` first; else amenity overlay on active permits. */
export function getPermitStatusBadgePropsForDisplay(
  status: PermanentParkingPermitStatus,
  source: Parameters<typeof resolveRowAmenityAccessRestricted>[0],
): {label: string; variant: BadgeProps["variant"]} {
  if (status === "suspended") {
    return getPermitStatusBadgeProps("suspended");
  }
  if (resolveRowAmenityAccessRestricted(source) && status === "active") {
    return getPermitStatusBadgeProps("suspended");
  }
  return getPermitStatusBadgeProps(status);
}

/** Shown under Suspended when coverage has not started yet. */
export const SUSPENDED_FUTURE_COVERAGE_QUALIFIER = "Future";

export function isPermitDisplayedAsSuspended(
  status: PermanentParkingPermitStatus,
  source: AmenityRestrictionRowSource | null | undefined,
): boolean {
  return getPermitStatusBadgePropsForDisplay(status, source).label === "Suspended";
}

export function isSuspendedFuturePermit(
  permit: Pick<PermanentParkingPermit, "status" | "start_date" | "end_date"> &
    AmenityRestrictionRowSource,
  timeZone: string,
): boolean {
  if (!isPermitDisplayedAsSuspended(permit.status, permit)) return false;
  return getPermanentPermitCoverageStatus(permit.start_date, permit.end_date, timeZone) === "scheduled";
}

export function getSuspendedFuturePermitQualifierLabel(
  permit: Pick<PermanentParkingPermit, "start_date">,
  timeZone: string,
): string {
  return getPermanentPermitStartHumanLabel(permit.start_date, timeZone)?.trim() ?? SUSPENDED_FUTURE_COVERAGE_QUALIFIER;
}

/** Maps GET vehicles `permanent_permits.status_label` to the same badge styling as permit tables. */
export function getPermitStatusBadgePropsFromApiLabel(
  statusLabel: string | null | undefined,
): {label: string; variant: BadgeProps["variant"]} | null {
  const raw = statusLabel?.trim();
  if (!raw) return null;
  const key = raw.toLowerCase();
  const map: Partial<Record<string, PermanentParkingPermitStatus>> = {
    active: "active",
    suspended: "suspended",
    revoked: "revoked",
    scheduled: "scheduled",
    expired: "expired",
  };
  const status = map[key];
  return status ? getPermitStatusBadgeProps(status) : {label: raw, variant: "secondary"};
}

export type PermanentPermitsEmbedStatusSource = {
  active?: number | null;
  status?: string | null;
  status_label?: string | null;
  suspended?: number | null;
  suspended_count?: number | null;
};

function readSuspendedPermitEmbedCount(meta: PermanentPermitsEmbedStatusSource): number {
  const raw = meta.suspended ?? meta.suspended_count ?? 0;
  return typeof raw === "number" && raw > 0 ? raw : 0;
}

/** No active or suspended permit on vehicle per GET vehicles embed counts. */
export function isPermanentPermitsEmbedCountVacant(
  meta: PermanentPermitsEmbedStatusSource | null | undefined,
): boolean {
  if (!meta) return false;
  return (meta.active ?? 0) <= 0 && readSuspendedPermitEmbedCount(meta) === 0;
}

function normalizePermitEmbedStatus(
  status: string | null | undefined,
): PermanentParkingPermitStatus | null {
  const key = status?.trim().toLowerCase();
  if (!key) return null;
  if (
    key === "active" ||
    key === "suspended" ||
    key === "revoked" ||
    key === "scheduled" ||
    key === "expired"
  ) {
    return key;
  }
  return null;
}

/** Maps GET vehicles `permanent_permits` embed to list/KPI badge when parent lifecycle is known. */
export function getPermanentPermitsEmbedStatusBadgeProps(
  meta: PermanentPermitsEmbedStatusSource | null | undefined,
): {label: string; variant: BadgeProps["variant"]} | null {
  if (!meta) return null;

  const fromLabel = getPermitStatusBadgePropsFromApiLabel(meta.status_label);
  if (fromLabel) return fromLabel;

  const fromStatus = normalizePermitEmbedStatus(meta.status);
  if (fromStatus) {
    return getPermitStatusBadgeProps(fromStatus);
  }

  if (readSuspendedPermitEmbedCount(meta) > 0) {
    return getPermitStatusBadgeProps("suspended");
  }

  return null;
}

/** True when any loaded permit row has API parent lifecycle suspended. */
export function hasSuspendedPermitInRows(
  permits: Pick<PermanentParkingPermit, "status">[] | null | undefined,
): boolean {
  if (!permits?.length) return false;
  return permits.some((row) => row.status === "suspended");
}
